import { interval as observableInterval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { Component, Inject, OnDestroy, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, AbstractControl } from '@angular/forms';
import { AuthService, IAuthResponse as AuthResponse } from './../auth/auth.service';

import { NgbModal, NgbModalRef, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { NgbdBankIdModal } from '../modal/bank-id.modal.component';
import { ErrorModalService } from '../modal/error.modal.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ParamMap, ActivatedRoute, Router } from '@angular/router';
import { personalNumberValidator } from '../_validators/personal-number.validator';
import { Instance } from '../interfaces/instance.interface';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SessionService } from '../session/session.service';

@Component({
    selector: 'app-admin',
    templateUrl: './admin.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AdminComponent implements OnDestroy {
  constructor(
    protected auth: AuthService,
    protected sessionService: SessionService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected modalService: NgbModal,
    protected errorModalService: ErrorModalService,
    protected fb: FormBuilder,
    protected spinner: NgxSpinnerService,
    protected deviceDetector: DeviceDetectorService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject('BANKID_INTERVAL') protected bankIdInterval: number) {

    const personalNumber: string = '';

    this.loginForm = this.fb.group({
      'personal_number': new FormControl(
        personalNumber,
        [
          personalNumberValidator
        ])
    });

    this.auth.isLoggedIn().subscribe(
      (isLoggedIn: boolean) => {
        if (isLoggedIn) {
          this.autoRoute();
        }
      },
      (error) => {

      });
  }

  public results: string;
  public errorMessage: string;

  private orderRef: string;
  private lockedWindow: NgbModalRef;
  private collectionLoop: Subscription;
  private ongoingTransaction: boolean;


  private qrExpiresIn = 300;
  private qrRemaining: number;
  private qrCountdownInterval: any;
  public loginForm: FormGroup;

  public get personal_number(): AbstractControl { return this.loginForm.get('personal_number'); }

  public openModal(content, windowClass?: NgbModalOptions) {
    if (this.lockedWindow) {
      this.lockedWindow.close();
    }

    this.lockedWindow = this.modalService.open(content, windowClass);

    this.lockedWindow.result.then(() => {
      this.cancelBankId();
    });
  }

  private autoRoute() {
    this.route.queryParamMap.subscribe(
      (params: ParamMap) => {
        const navigateTo = params.get('navigate-to');
        const instanceId = params.get('instance');
        const roleType = params.get('role');
        if (navigateTo && instanceId && roleType) {
          this.spinner.show();
          this.sessionService.getInstances().subscribe(
            instanceSelector => {
              const instance: Instance = instanceSelector.instances.find(i =>
                i.id === instanceId &&
                i.roleType === roleType);
              if (instance) {
                this.sessionService.setInstance(instance, navigateTo);
              } else {
                this.sessionService.selectInstance();
              }
            }, error => {
              if (location.pathname.includes('borgenar'))
                this.router.navigate(['/borgenar']);
              else
                this.router.navigate(['/home']);
            }, () => {
              this.spinner.hide();
            });
        } else {
          this.sessionService.setInstance(null, '/select-instance');
        }
      });
  }

  private openBankIdModal() {
    this.openModal(NgbdBankIdModal, { backdrop: 'static', keyboard: false, centered: true });
    if (this.lockedWindow && this.lockedWindow.componentInstance) {
      this.lockedWindow.componentInstance.qrRemaining = this.qrRemaining;
      this.lockedWindow.componentInstance.extendTimeoutClicked.subscribe(() => {
        this.extendQrCode();
      });
    }
  }

  public extendQrCode() {
    this.qrRemaining = this.qrExpiresIn;
  }

  public openErrorModal(title: string, message: string) {
    this.errorModalService.open(title, message, () => { }, this.lockedWindow);
  }

  public loginBankIdOnFile() {
    this.spinner.show();
    this.auth.authAdminBankId(this.personal_number.value).subscribe(
      authResponse => {
        this.spinner.hide();
        this.openBankIdModal();
        this.lockedWindow.componentInstance.isMobileBankId = false;
        this.ongoingTransaction = true;
        this.startBankIdClient(authResponse.autoStartToken);
        this.orderRef = authResponse.orderRef;
        this.collectLoop(); // starta collectloopen
      },
      error => {
        this.spinner.hide();
        this.openErrorModal('Inloggningsfel', error.error.exceptionMessage);
        this.auth.logout();
      });
  }

  public loginMobileBankId() {
    this.spinner.show();
    this.openBankIdModal();

    if (this.lockedWindow && this.lockedWindow.componentInstance) {
      this.lockedWindow.componentInstance.isMobileBankId = true;
      this.lockedWindow.componentInstance.showQrCode = !this.deviceDetector.isMobile();
    }

    this.startTotalCountdown();
    this.startNewBankIdSession();

    this.spinner.hide();
  }

  private startTotalCountdown() {
    this.qrRemaining = this.qrExpiresIn;
    if (this.qrCountdownInterval) {
      clearInterval(this.qrCountdownInterval);
    }

    this.qrCountdownInterval = setInterval(() => {
      this.qrRemaining--;
      if (this.lockedWindow && this.lockedWindow.componentInstance) {
        this.lockedWindow.componentInstance.qrRemaining = this.qrRemaining;
      }

      if (this.qrRemaining <= 0) {
        clearInterval(this.qrCountdownInterval);

        if (this.lockedWindow && this.lockedWindow.componentInstance) {
          this.lockedWindow.componentInstance.hintCode = 'expiredTransaction';
          this.lockedWindow.componentInstance.status = 'failed';
          this.lockedWindow.componentInstance.showBankIdLink = true;
          this.ongoingTransaction = false;
          this.unsubscribeCollectionLoop();
        }
      }
    }, 1000);
  }

  private startNewBankIdSession() {
    this.auth.authAdminMobileBankId(this.personal_number.value).subscribe(
      authResponse => {
        this.orderRef = authResponse.orderRef;
        this.unsubscribeCollectionLoop();
        this.ongoingTransaction = true;
        this.collectLoop();

        if (this.deviceDetector.isMobile() && this.lockedWindow && this.lockedWindow.componentInstance)
          this.startBankIdClient(authResponse.autoStartToken);
      },
      error => {
        this.openErrorModal('Inloggningsfel', error.error.exceptionMessage);
        if (this.lockedWindow) {
          this.lockedWindow.close();
        }
      }
    );
  }

  private collectLoop() {
    let lastCollectHandled = true;
    this.collectionLoop = observableInterval(this.bankIdInterval).pipe(
      takeWhile(() => this.ongoingTransaction)).subscribe(
        () => {
          if (!lastCollectHandled) {
            return;
          }

          lastCollectHandled = false;

          this.auth.collectBankId(this.orderRef).subscribe(
            collectResponse => {

              if (collectResponse.status === 'failed'
                && this.lockedWindow?.componentInstance?.isMobileBankId === true
                && (collectResponse.hintCode === 'startFailed' || collectResponse.hintCode === 'expiredTransaction')
                && this.qrRemaining > 0) {
                this.unsubscribeCollectionLoop();
                this.startNewBankIdSession();
                return;
              }

              lastCollectHandled = true;
              if (this.lockedWindow && this.lockedWindow.componentInstance) {
                this.lockedWindow.componentInstance.hintCode = collectResponse.hintCode;
                this.lockedWindow.componentInstance.status = collectResponse.status;
                if (this.lockedWindow.componentInstance.isMobileBankId && collectResponse.qrCode) {
                  this.lockedWindow.componentInstance.qrCode = collectResponse.qrCode;
                }
              }

              if (collectResponse.status === 'complete') {
                if (collectResponse.completionData.accessToken) {
                  const loginMethod: string = this.lockedWindow.componentInstance.isMobileBankId ? this.deviceDetector.isMobile() ? 'mobile-pin' : 'mobile-qr' : 'pc';
                  this.auth.setToken(collectResponse.completionData.accessToken);
                  this.sessionService.setLoginMethod(loginMethod);
                  this.autoRoute();
                }

                this.lockedWindow.close();
              }

              this.ongoingTransaction = collectResponse.status === 'pending';
            },
            error => {
              this.ongoingTransaction = false;
              this.unsubscribeCollectionLoop();
              this.openErrorModal('Inloggningsfel', error.error.exceptionMessage);
            });
        },
        () => this.ongoingTransaction = false);
  }

  private cancelBankId() {
    if (this.ongoingTransaction) {
      this.auth.cancelBankId(this.orderRef).subscribe(() => this.unsubscribeCollectionLoop());
    } else {
      this.unsubscribeCollectionLoop();
    }

    if (this.qrCountdownInterval) {
      clearInterval(this.qrCountdownInterval);
      this.qrCountdownInterval = null;
    }
  }

  private unsubscribeCollectionLoop() {
    if (!!this.collectionLoop && !this.collectionLoop.closed) {
      this.collectionLoop.unsubscribe();
    }
  }

  private startBankIdClient(token: string, returnUrl: string = null) {
    this.document.location.href = `bankid:///?autostarttoken=${token}&redirect=${returnUrl ? returnUrl : ''}`;
  }

  ngOnDestroy() {
    this.cancelBankId();
  }
}
